import { isCorporateClientsAvailable } from "../../companyClients/lib/clientLabels";
import { isCorporatePropertiesAvailable } from "../../companyProperties/lib/propertyLabels";
import { isAdminMembership } from "../../companyWorkspace/lib/companyLabels";

/**
 * Authoritative pagination count helper.
 * Never uses results.length as a metric value.
 */
export function authoritativeListCount(payload) {
  if (payload == null) return null;
  if (typeof payload.count === "number" && Number.isFinite(payload.count)) {
    return payload.count;
  }
  return null;
}

/**
 * Metric catalog for Company operational overview (I2).
 * Deals are intentionally omitted — no canonical company-scoped count source.
 */
export const COMPANY_DEAL_METRIC =
  "OMITTED_NO_CANONICAL_COMPANY_SCOPED_COUNT_SOURCE";

export function canSeeManagementClientQueue(company, membership) {
  return (
    isAdminMembership(company, membership) &&
    isCorporateClientsAvailable(company)
  );
}

export function canSeeManagementPropertyQueue(company, membership) {
  return (
    isAdminMembership(company, membership) &&
    isCorporatePropertiesAvailable(company)
  );
}

export function buildOverviewMetricPlan(company, membership, options = {}) {
  const companyId = company?.id;
  const financeEnabled = options.financeEnabled === true;
  const financeBooks =
    options.canAccessFinanceBooks === true && financeEnabled;
  const showClientQueue = options.canSeeClientQueue === true;
  const showPropertyQueue = options.canSeePropertyQueue === true;

  const metrics = [
    {
      id: "active_members",
      label: "Активні учасники",
      description: "Активні членства цієї компанії",
      kind: "company_field",
      field: "members_count",
      drillDown: `/companies/${companyId}/members`,
      required: true,
    },
    {
      id: "clients_visible",
      label: "Клієнти, доступні мені",
      description:
        "Лише корпоративні клієнти, які вам дозволено бачити",
      kind: "paginated",
      source: "clients",
      drillDown: `/companies/${companyId}/clients`,
      required: true,
    },
    {
      id: "properties_visible",
      label: "Об'єкти, доступні мені",
      description:
        "Корпоративні об'єкти, доступні вам за поточними правами",
      kind: "paginated",
      source: "properties",
      drillDown: `/companies/${companyId}/properties`,
      required: true,
    },
    {
      id: "tasks_todo",
      label: "Задачі: заплановано",
      description: "Корпоративні задачі зі статусом «заплановано»",
      kind: "paginated",
      source: "tasks_todo",
      drillDown: `/companies/${companyId}/tasks?status=todo`,
      required: true,
    },
    {
      id: "tasks_in_progress",
      label: "Задачі: в роботі",
      description: "Корпоративні задачі зі статусом «в роботі»",
      kind: "paginated",
      source: "tasks_in_progress",
      drillDown: `/companies/${companyId}/tasks?status=in_progress`,
      required: true,
    },
    {
      id: "tasks_overdue",
      label: "Прострочені задачі",
      description: "Корпоративні задачі з простроченим терміном",
      kind: "paginated",
      source: "tasks_overdue",
      drillDown: `/companies/${companyId}/tasks?is_overdue=true`,
      required: false,
    },
    {
      id: "viewings_scheduled",
      label: "Заплановані перегляди",
      description: "Корпоративні перегляди зі статусом «заплановано»",
      kind: "paginated",
      source: "viewings_scheduled",
      drillDown: `/companies/${companyId}/tasks?kind=viewing&status=scheduled`,
      required: true,
    },
  ];

  if (showClientQueue) {
    metrics.push({
      id: "clients_unassigned",
      label: "Непризначені клієнти",
      description: "Черга клієнтів без відповідального",
      kind: "paginated",
      source: "clients_unassigned",
      optionalAuthority: true,
      drillDown: `/companies/${companyId}/clients/unassigned`,
      required: false,
    });
  }

  if (showPropertyQueue) {
    metrics.push({
      id: "properties_unassigned",
      label: "Непризначені об'єкти",
      description: "Черга об'єктів без відповідального",
      kind: "paginated",
      source: "properties_unassigned",
      optionalAuthority: true,
      drillDown: `/companies/${companyId}/properties/unassigned`,
      required: false,
    });
  }

  if (financeBooks) {
    metrics.push(
      {
        id: "finance_receipts",
        label: "Надходження",
        description: "Кількість записів надходжень компанії",
        kind: "paginated",
        source: "finance_receipts",
        optionalAuthority: true,
        finance: true,
        drillDown: `/companies/${companyId}/finance`,
        required: false,
      },
      {
        id: "finance_obligations",
        label: "Зобов'язання",
        description: "Кількість записів зобов'язань компанії",
        kind: "paginated",
        source: "finance_obligations",
        optionalAuthority: true,
        finance: true,
        drillDown: `/companies/${companyId}/finance`,
        required: false,
      },
    );
  }

  return metrics;
}
